import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { getFileUrl } from '../utils/storageService';
import MobileChatPicker from './MobileChatPicker';
import './MobileBottomNav.css';

// Reuse the same icons used in Sidebar but inline (small SVGs).
const items = [
  {
    name: 'Home',
    path: '/',
    icon: (
      <svg width="22" height="22" fill="none" viewBox="0 0 24 24">
        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m4 12 8-8 8 8M6 10.5V19a1 1 0 0 0 1 1h3v-3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v3h3a1 1 0 0 0 1-1v-8.5"/>
      </svg>
    ),
  },
  {
    name: 'Students',
    path: '/students',
    icon: (
      <svg width="22" height="22" fill="currentColor" viewBox="0 0 16 16">
        <path d="M7 14s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1zm4-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6m-5.784 6A2.24 2.24 0 0 1 5 13c0-1.355.68-2.75 1.936-3.72A6.3 6.3 0 0 0 5 9c-4 0-5 3-5 4s1 1 1 1zM4.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5"/>
      </svg>
    ),
  },
  {
    name: 'Chat',
    isChat: true,
    icon: (
      <svg width="22" height="22" fill="currentColor" viewBox="0 0 64 64">
        <path fillRule="evenodd" clipRule="evenodd" d="m2 16c0-7.73199 6.26801-14 14-14h32c7.732 0 14 6.26801 14 14v44c0 .7523-.4221 1.4409-1.0924 1.7822-.6704.3414-1.4756.2777-2.0839-.1647l-10.4741-7.6175h-32.3496c-7.73199 0-14-6.268-14-14zm15 2c-1.1046 0-2 .8954-2 2s.8954 2 2 2h30c1.1046 0 2-.8954 2-2s-.8954-2-2-2zm0 12c-1.1046 0-2 .8954-2 2s.8954 2 2 2h12c1.1046 0 2-.8954 2-2s-.8954-2-2-2z"/>
      </svg>
    ),
  },
  {
    name: 'Schedule',
    path: '/schedule',
    icon: (
      <svg width="22" height="22" fill="currentColor" viewBox="0 0 24 24">
        <path fillRule="evenodd" d="M5 5a1 1 0 0 0 1-1 1 1 0 1 1 2 0 1 1 0 0 0 1 1h1a1 1 0 0 0 1-1 1 1 0 1 1 2 0 1 1 0 0 0 1 1h1a1 1 0 0 0 1-1 1 1 0 1 1 2 0 1 1 0 0 0 1 1 2 2 0 0 1 2 2v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a2 2 0 0 1 2-2ZM3 19v-7a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" clipRule="evenodd"/>
      </svg>
    ),
  },
  {
    name: 'Profile',
    path: '/profile-settings',
    icon: (
      <svg width="22" height="22" fill="none" viewBox="0 0 24 24">
        <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2"/>
        <path stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M4 21c0-4 4-7 8-7s8 3 8 7"/>
      </svg>
    ),
  },
];

export default function MobileBottomNav() {
  const { pathname } = useLocation();
  // Hide inside lesson workspace — that page has its own bottom tab bar
  // (doc / whiteboard / homework) which would overlap with this nav.
  const hideOnLessonRoutes = /^\/lessons\/[^/]+/.test(pathname);

  // Already on a student's profile → open that student's chat right away
  // (StudentProfile.js listens for this event). Otherwise show an inline
  // picker sheet so the teacher doesn't have to detour through /students.
  const studentProfileMatch = pathname.match(/^\/students\/([^/]+)$/);
  const onStudentProfile = studentProfileMatch && studentProfileMatch[1] !== 'new';
  const [pickerOpen, setPickerOpen] = useState(false);
  const handleChatClick = () => {
    if (onStudentProfile) {
      window.dispatchEvent(new CustomEvent('novas:openMobileChat', {
        detail: { studentSlug: studentProfileMatch[1] },
      }));
    } else {
      setPickerOpen(true);
    }
  };

  // Instagram-style shrink-on-scroll-down / grow-on-scroll-up.
  // .main-content-area has overflow-y:auto in App.css, but .app-layout
  // only sets min-height:100vh (not a fixed height), so the content
  // area's own height just grows to fit its content instead of ever
  // actually overflowing — the real scrolling happens on window.
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    let lastY = window.scrollY;
    let raf = null;
    const onScroll = () => {
      if (raf !== null) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const y = window.scrollY;
        const dy = y - lastY;
        // Small dead zone so a tiny wobble at rest (or a bounce/rubber-
        // band overscroll) doesn't flip it back and forth.
        if (Math.abs(dy) > 4) {
          setCompact(dy > 0 && y > 24);
          lastY = y;
        }
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, []);

  // Teacher's own avatar for the Profile tab, Instagram-style — same
  // fetch pattern ProfileSettings.jsx already uses (teachers.photo,
  // either a full URL already or a storage path needing a signed URL).
  const [avatarUrl, setAvatarUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (!authUser) return;
        const { data: teacherData, error } = await supabase
          .from('teachers')
          .select('photo')
          .eq('auth_user_id', authUser.id)
          .single();
        if (error || !teacherData?.photo || cancelled) return;
        if (teacherData.photo.startsWith('http')) {
          setAvatarUrl(teacherData.photo);
        } else {
          const url = await getFileUrl(teacherData.photo, 31536000);
          if (!cancelled) setAvatarUrl(url);
        }
      } catch {
        // No avatar — the Profile tab just keeps its default icon.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (hideOnLessonRoutes) return null;

  return (
    <>
    <nav className={`mobile-bottom-nav${compact ? ' is-compact' : ''}`} aria-label="Primary">
      {items.map(item => (
        item.isChat ? (
          // Never marked "active" — unlike the other tabs it isn't a
          // page of its own, and highlighting it just because the
          // teacher navigated to a student profile some other way (e.g.
          // from the Students list) reads as if Chat had been tapped.
          <button
            key={item.name}
            type="button"
            className="mobile-bottom-nav-link"
            onClick={handleChatClick}
          >
            <span className="mobile-bottom-nav-icon">{item.icon}</span>
            <span className="mobile-bottom-nav-label">{item.name}</span>
          </button>
        ) : (
          <NavLink
            key={item.name}
            to={item.path}
            end={item.path === '/'}
            className={({ isActive }) => 'mobile-bottom-nav-link' + (isActive ? ' active' : '')}
          >
            <span className="mobile-bottom-nav-icon">
              {item.name === 'Profile' && avatarUrl ? (
                <img src={avatarUrl} alt="" className="mobile-bottom-nav-avatar" />
              ) : item.icon}
            </span>
            <span className="mobile-bottom-nav-label">{item.name}</span>
          </NavLink>
        )
      ))}
    </nav>
    {pickerOpen && <MobileChatPicker onClose={() => setPickerOpen(false)} />}
    </>
  );
}
